import React, { useState, useEffect, useCallback } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, FileText, RefreshCw } from 'lucide-react';

const NOTE_TYPES = ['GENERAL', 'DAILY_LIVING', 'COMMUNITY', 'BEHAVIOUR', 'MEDICATION', 'TRANSPORT', 'SIL', 'CENTRE'];
const emptyForm = { name: '', noteType: 'GENERAL', active: true, content: '', supportProvided: '', outcomeObserved: '', concern: '' };

function TemplateDialog({ open, onClose, template, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (template) {
        const f = template.fields || {};
        setForm({
          name: template.name || '', noteType: template.noteType || 'GENERAL',
          active: template.active !== false,
          content: f.content || '', supportProvided: f.supportProvided || '',
          outcomeObserved: f.outcomeObserved || '', concern: f.concern || '',
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, template]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), noteType: form.noteType, active: form.active,
        fields: {
          content: form.content, supportProvided: form.supportProvided,
          outcomeObserved: form.outcomeObserved, concern: form.concern,
        },
      };
      if (template) {
        await api.put(`/mobile/admin/note-templates/${template.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/mobile/admin/note-templates', payload);
        toast.success('Template created');
      }
      onSaved(); onClose();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="note-template-dialog">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Note Template' : 'New Note Template'}</DialogTitle>
          <DialogDescription>Prefilled fields are editable by staff before saving a note.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input data-testid="nt-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Evening SIL routine" />
            </div>
            <div className="space-y-2">
              <Label>Note type</Label>
              <Select value={form.noteType} onValueChange={(v) => setForm({ ...form, noteType: v })}>
                <SelectTrigger data-testid="nt-type"><SelectValue /></SelectTrigger>
                <SelectContent>{NOTE_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Prefill — note content</Label>
            <Textarea rows={2} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="nt-content" />
          </div>
          <div className="space-y-2">
            <Label>Prefill — support provided</Label>
            <Textarea rows={2} value={form.supportProvided} onChange={(e) => setForm({ ...form, supportProvided: e.target.value })} data-testid="nt-support" />
          </div>
          <div className="space-y-2">
            <Label>Prefill — outcome observed</Label>
            <Textarea rows={2} value={form.outcomeObserved} onChange={(e) => setForm({ ...form, outcomeObserved: e.target.value })} data-testid="nt-outcome" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="nt-active" />
            <Label className="cursor-pointer">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="nt-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="nt-save">{saving ? 'Saving…' : template ? 'Save changes' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function NoteTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/mobile/admin/note-templates');
      setTemplates(res.data?.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/mobile/admin/note-templates/${deleteTarget.id}`);
      toast.success('Template deleted'); setDeleteTarget(null); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="note-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="h-6 w-6" /> Progress Note Templates</h1>
          <p className="text-muted-foreground">Custom note templates for support workers. Built-in templates are always available in the mobile app.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="nt-refresh"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="nt-new"><Plus className="h-4 w-4 mr-2" /> New Template</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="nt-empty">No custom templates yet. The 8 built-in templates are already available to staff.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {templates.map(t => (
                  <TableRow key={t.id} data-testid={`nt-row-${t.id}`}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell><Badge variant="secondary">{(t.noteType || 'GENERAL').replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell><Badge variant={t.active ? 'default' : 'secondary'}>{t.active ? 'Active' : 'Inactive'}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(t); setDialogOpen(true); }} data-testid={`nt-edit-${t.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(t)} data-testid={`nt-delete-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TemplateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} template={editing} onSaved={load} />
      <ConfirmDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete note template?" description={`"${deleteTarget?.name}" will be removed.`}
        confirmLabel="Delete" variant="destructive" onConfirm={handleDelete} />
    </div>
  );
}
